import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Drawer,
  IconButton,
  MenuItem,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { TabularText } from '@/components/TabularText';
import { CloseIcon } from '@/components/icons';
import { useTranslation } from 'react-i18next';
import { fxRatesApi, type FxRate } from '@/api/fxRates';
import { ErrorState } from '@/components/ErrorState';
import { formatCivilDate, formatInstant, formatNumber } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { toCivil } from '@/features/envelope/window';

export function fxPeriod(days: number, today = new Date()) {
  return {
    from: toCivil(new Date(today.getFullYear(), today.getMonth(), today.getDate() - days + 1)),
    to: toCivil(today),
  };
}

export function FxRatesDrawer({
  base,
  quote,
  onClose,
}: {
  base: string;
  quote: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const [preset, setPreset] = useState('90');
  const [range, setRange] = useState(() => fxPeriod(90));
  const [retry, setRetry] = useState(0);
  const [state, setState] = useState<{
    status: 'loading' | 'error' | 'ready';
    rows: FxRate[];
    error?: unknown;
  }>({
    status: 'loading',
    rows: [],
  });
  const valid = range.from !== '' && range.to !== '' && range.from <= range.to;
  useEffect(() => {
    if (!valid) return;
    let cancelled = false;
    void fxRatesApi
      .list({ base, quote, ...range })
      .then((rows) => {
        if (!cancelled)
          setState({ status: 'ready', rows: rows.sort((a, b) => b.asOf.localeCompare(a.asOf)) });
      })
      .catch((error: unknown) => {
        if (!cancelled) setState({ status: 'error', rows: [], error });
      });
    return () => {
      cancelled = true;
    };
  }, [base, quote, range, retry, valid]);
  return (
    <Drawer
      open
      anchor="right"
      onClose={onClose}
      slotProps={{
        paper: {
          role: 'dialog',
          'aria-modal': true,
          'aria-labelledby': 'fx-rates-title',
          sx: { width: { xs: '100%', md: 560 }, maxWidth: '100%' },
        },
      }}
    >
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography id="fx-rates-title" variant="h2" sx={{ flex: 1 }}>
            {t('fxRates.title', { base, quote })}
          </Typography>
          <IconButton aria-label={t('common.close')} onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <TextField
          select
          fullWidth
          label={t('fxRates.period')}
          value={preset}
          onChange={(event) => {
            const next = event.target.value;
            setPreset(next);
            if (next !== 'custom') {
              setState({ status: 'loading', rows: [] });
              setRange(fxPeriod(Number(next)));
            }
          }}
        >
          {['30', '90', '365'].map((days) => (
            <MenuItem key={days} value={days}>
              {t('fxRates.days', { days })}
            </MenuItem>
          ))}
          <MenuItem value="custom">{t('fxRates.custom')}</MenuItem>
        </TextField>
        {preset === 'custom' && (
          <Stack direction={{ xs: 'column', md: 'row' }} sx={{ gap: 2, mt: 2 }}>
            <TextField
              type="date"
              label={t('fxRates.from')}
              value={range.from}
              slotProps={{ inputLabel: { shrink: true } }}
              onChange={(e) => {
                setState({ status: 'loading', rows: [] });
                setRange({ ...range, from: e.target.value });
              }}
            />
            <TextField
              type="date"
              label={t('fxRates.to')}
              value={range.to}
              slotProps={{ inputLabel: { shrink: true } }}
              onChange={(e) => {
                setState({ status: 'loading', rows: [] });
                setRange({ ...range, to: e.target.value });
              }}
            />
          </Stack>
        )}
        <Typography variant="body2" sx={{ my: 2, color: 'text.secondary' }}>
          {t('fxRates.basis', { base, quote })}
        </Typography>
        {!valid ? (
          <Alert severity="warning">{t('fxRates.invalidRange')}</Alert>
        ) : state.status === 'error' ? (
          <ErrorState
            variant="section"
            error={state.error}
            what={t('errors.what.fxRates')}
            onRetry={() => {
              setState({ status: 'loading', rows: [] });
              setRetry((n) => n + 1);
            }}
          />
        ) : state.status === 'ready' && state.rows.length === 0 ? (
          <Typography>{t('fxRates.empty')}</Typography>
        ) : (
          <Table aria-label={t('fxRates.table')} size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('fxRates.date')}</TableCell>
                <TableCell align="right">{t('fxRates.rate')}</TableCell>
                <TableCell>{t('fxRates.source')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {state.status === 'loading'
                ? [0, 1, 2].map((key) => (
                    <TableRow key={key}>
                      {[0, 1, 2].map((col) => (
                        <TableCell key={col}>
                          <Skeleton />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : state.rows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>
                        <Tooltip
                          describeChild
                          title={t('fxRates.fetchedAt', {
                            exact: row.fetchedAt,
                            instant: formatInstant(row.fetchedAt, locale, 'UTC', {
                              dateStyle: 'full',
                              timeStyle: 'long',
                            }),
                          })}
                        >
                          <Typography
                            component="span"
                            tabIndex={0}
                            sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
                          >
                            {formatCivilDate(row.asOf, locale)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <TabularText>
                          {formatNumber(row.rate, locale, {
                            maximumFractionDigits: 100,
                            minimumFractionDigits: Math.min(
                              100,
                              row.rate.split('.')[1]?.length ?? 0,
                            ),
                          })}
                        </TabularText>
                      </TableCell>
                      <TableCell sx={{ overflowWrap: 'anywhere' }}>{row.source}</TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        )}
      </Box>
    </Drawer>
  );
}
